import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@tanstack/react-query';
import { useClientes } from '../../../hooks/useClientes';
import {
  fetchOneMinutePaperEvidenceBlob,
  fetchOneMinutePaperReport,
  OneMinutePaperEvidenceMeta,
  OneMinutePaperReportRow,
} from '../../../api/oneMinutePaper';
import { enqueueSnackbar } from '../../../components/utils/snackbar';

export default function OneMinutePaperAlumnoPage() {
  const today = dayjs().format('YYYY-MM-DD');
  const [desde, setDesde] = useState(today);
  const [hasta, setHasta] = useState(today);
  const [alumnoSel, setAlumnoSel] = useState<number>();
  const [searchCli, setSearchCli] = useState('');
  const [searchProf, setSearchProf] = useState('');
  const [submittedAt, setSubmittedAt] = useState(0);

  const [openDetail, setOpenDetail] = useState<OneMinutePaperReportRow | null>(null);
  const [imgUrls, setImgUrls] = useState<string[]>([]);
  const [loadingImgs, setLoadingImgs] = useState(false);

  const { rows: clientes = [] } = useClientes();

  const reportQuery = useQuery({
    queryKey: ['oneMinutePaperReport', alumnoSel, desde, hasta, submittedAt],
    queryFn: () =>
      fetchOneMinutePaperReport({
        alumnoId: Number(alumnoSel),
        desde,
        hasta,
      }),
    enabled: !!alumnoSel && submittedAt > 0,
  });

  const clientesFiltrados = useMemo(
    () => clientes.filter((c) => c.nombreCompleto.toLowerCase().includes(searchCli.toLowerCase())),
    [clientes, searchCli],
  );

  const filas = useMemo(() => {
    const rows = reportQuery.data?.rows ?? [];
    if (!searchProf.trim()) return rows;
    return rows.filter((r) => r.profesor.toLowerCase().includes(searchProf.toLowerCase()));
  }, [reportQuery.data?.rows, searchProf]);

  useEffect(() => {
    let cancelled = false;
    const urlsToRevoke: string[] = [];

    const loadImages = async (entryId: number, evidencias: OneMinutePaperEvidenceMeta[]) => {
      try {
        setLoadingImgs(true);
        setImgUrls([]);

        const blobs = await Promise.all(
          evidencias.map((e) => fetchOneMinutePaperEvidenceBlob(entryId, e.index)),
        );

        if (cancelled) return;

        const urls = blobs.map((b) => URL.createObjectURL(b));
        urls.forEach((u) => urlsToRevoke.push(u));
        setImgUrls(urls);
      } catch (error: any) {
        const msg =
          error?.response?.data?.message ?? error?.message ?? 'No se pudieron cargar evidencias';
        enqueueSnackbar(String(msg), { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingImgs(false);
      }
    };

    if (openDetail?.oneMinutePaperId && openDetail.evidencias.length > 0) {
      loadImages(openDetail.oneMinutePaperId, openDetail.evidencias);
    } else {
      setImgUrls([]);
      setLoadingImgs(false);
    }

    return () => {
      cancelled = true;
      urlsToRevoke.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [openDetail]);

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Ejercicio de inicio de clase por alumno</Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          label="Desde"
          type="date"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />

        <TextField
          label="Hasta"
          type="date"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />

        <TextField
          size="small"
          placeholder="Buscar alumno"
          value={searchCli}
          onChange={(e) => setSearchCli(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ ml: 2, width: 260 }}
        />
      </Stack>

      <Paper sx={{ maxHeight: 280, overflowY: 'auto', p: 1 }}>
        <Table size="small">
          <TableBody>
            {clientesFiltrados.map((c) => (
              <TableRow
                key={c.id}
                hover
                selected={c.id === alumnoSel}
                onClick={() => setAlumnoSel(c.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{c.nombreCompleto}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          disabled={!alumnoSel}
          onClick={() => setSubmittedAt(Date.now())}
          sx={{
            backgroundColor: '#21a598',
            '&:hover': { backgroundColor: '#1a8578' },
            boxShadow: 'none',
          }}
        >
          Buscar
        </Button>

        <TextField
          size="small"
          placeholder="Filtrar por profesor"
          value={searchProf}
          onChange={(e) => setSearchProf(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 260 }}
        />
      </Stack>

      {reportQuery.isFetching && <CircularProgress />}

      {reportQuery.data && (
        <Alert severity="info">Total reservas aplicables: {reportQuery.data.total}</Alert>
      )}

      {reportQuery.data && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Asignatura</TableCell>
                <TableCell>Profesor</TableCell>
                <TableCell align="right">Nota</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filas.map((r) => (
                <TableRow key={`${r.reservaId}-${r.profesorId}`}>
                  <TableCell>{r.fecha}</TableCell>
                  <TableCell>{r.asignatura}</TableCell>
                  <TableCell>{r.profesor}</TableCell>
                  <TableCell align="right">{r.nota != null ? r.nota : '—'}</TableCell>
                  <TableCell>{r.hasExercise ? 'Registrado' : 'Sin ejercicio'}</TableCell>
                  <TableCell align="right">
                    {r.hasExercise && r.oneMinutePaperId ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setOpenDetail(r)}
                      >
                        Ver ejercicio
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {filas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Sin datos para el filtro actual
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={!!openDetail} onClose={() => setOpenDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Ejercicio de inicio de clase - Reserva {openDetail?.reservaId ?? '—'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography>
              <strong>Fecha:</strong> {openDetail?.fecha} {openDetail?.horaInicio}
            </Typography>
            <Typography>
              <strong>Asignatura:</strong> {openDetail?.asignatura}
            </Typography>
            <Typography>
              <strong>Profesor:</strong> {openDetail?.profesor}
            </Typography>
            <Typography>
              <strong>Nota:</strong> {openDetail?.nota ?? '—'}
            </Typography>

            {loadingImgs && <CircularProgress size={20} />}

            {!loadingImgs && imgUrls.length === 0 && (
              <Alert severity="warning">No se encontraron evidencias</Alert>
            )}

            {!loadingImgs && imgUrls.length > 0 && (
              <Stack spacing={2}>
                {imgUrls.map((url, idx) => (
                  <Box
                    key={url}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                      Evidencia {idx + 1}: {openDetail?.evidencias[idx]?.fileName ?? 'imagen'}
                    </Typography>
                    <Box
                      component="img"
                      src={url}
                      alt={`Evidencia ${idx + 1}`}
                      sx={{ width: '100%', maxHeight: 480, objectFit: 'contain' }}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
